import React, { useEffect, useMemo, useRef, useState } from 'react';
import { tuitionPaid } from '../db';
import { getApiBase } from '../apiBase';

const API_BASE = getApiBase();
function NotificationBell({ db, setPage }) {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState([]);
  const [pendingAdm, setPendingAdm] = useState(0);
  const ref = React.useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Poll pending admissions count every 30s
  useEffect(() => {
    const fetchAdm = async () => {
      try {
        const token = sessionStorage.getItem('lkps_token') || '';
        const r = await fetch(`${API_BASE}/admissions`, { headers: { Authorization: 'Bearer ' + token } });
        const data = await r.json();
        if (Array.isArray(data)) setPendingAdm(data.filter(a => a.status === 'Pending').length);
      } catch { /* silent */ }
    };
    fetchAdm();
    const interval = setInterval(fetchAdm, 30000);
    return () => clearInterval(interval);
  }, []);

  const allNotes = React.useMemo(() => {
    if (!db) return [];
    const list = [];
    const today = new Date();

    // New admission enquiries pending
    if (pendingAdm > 0) list.push({
      id: 'adm', icon: 'how_to_reg', color: '#1960a3', bg: '#dbeafe',
      title: `${pendingAdm} new admission enquir${pendingAdm > 1 ? 'ies' : 'y'} pending`,
      sub: 'Tap to review and follow up',
      page: 'adm',
    });

    // Fee overdue — students with pending balance
    const overdue = (db.students || []).filter(s => {
      const cf = (db.settings?.classFees || {})[s.cls] || {};
      const monthly = parseFloat(cf.monthly) || 0;
      const annualFee = monthly > 0 ? monthly * 12 + (parseFloat(cf.exam) || 0) : 0;
      const totalConcession = (db.pays || []).filter(p => p.sid === s.id && p.concession).reduce((a, p) => a + (p.concession || 0), 0);
      const effectiveFee = Math.max(0, annualFee - totalConcession);
      const paid = tuitionPaid(db.pays || [], s.id);
      return effectiveFee > 0 && paid < effectiveFee;
    });
    if (overdue.length > 0) list.push({
      id: 'fee', icon: 'payments', color: '#ef4444', bg: '#fee2e2',
      title: `${overdue.length} student${overdue.length > 1 ? 's' : ''} have pending fees`,
      sub: overdue.slice(0, 2).map(s => `${s.fn || ''} ${s.ln || ''}`.trim()).join(', ') + (overdue.length > 2 ? ` +${overdue.length - 2} more` : ''),
      page: 'fee',
    });

    // Upcoming exams in next 7 days
    const upcoming = (db.exams || []).filter(e => {
      if (!e.st || e.st !== 'Upcoming') return false;
      if (!e.dt) return true;
      const d = new Date(e.dt);
      const diff = (d - today) / 86400000;
      return diff >= 0 && diff <= 7;
    });
    if (upcoming.length > 0) list.push({
      id: 'exam', icon: 'quiz', color: '#f59e0b', bg: '#fef3c7',
      title: `${upcoming.length} upcoming exam${upcoming.length > 1 ? 's' : ''}`,
      sub: upcoming.slice(0, 2).map(e => e.name || e.title || 'Exam').join(', '),
      page: 'exam',
    });

    // Low attendance — classes below 75%
    const lowAtt = (db.classes || []).filter(c => {
      const stuIds = (db.students || []).filter(s => s.cls === c.name).map(s => s.id);
      if (!stuIds.length) return false;
      let p = 0, t = 0;
      Object.values(db.att || {}).forEach(day => stuIds.forEach(sid => {
        if (day[sid] !== undefined) { t++; if (day[sid] === 'P' || day[sid] === 'L') p++; }
      }));
      return t > 0 && (p / t) * 100 < 75;
    });
    if (lowAtt.length > 0) list.push({
      id: 'att', icon: 'warning', color: '#8b5cf6', bg: '#ede9fe',
      title: `${lowAtt.length} class${lowAtt.length > 1 ? 'es' : ''} below 75% attendance`,
      sub: lowAtt.map(c => c.name).join(', '),
      page: 'att',
    });

    // No students enrolled
    if ((db.students || []).length === 0) list.push({
      id: 'nostu', icon: 'group_add', color: '#1960a3', bg: '#dbeafe',
      title: 'No students enrolled yet',
      sub: 'Add students to get started',
      page: 'stu',
    });

    return list;
  }, [db, pendingAdm]);

  const notes = allNotes.filter(n => !dismissed.includes(n.id));
  const count = notes.length;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ position: 'relative', color: open ? '#1960a3' : '#64748b', border: 0, background: open ? '#eef4ff' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', borderRadius: 8, padding: 6, transition: 'all 150ms' }}
        onMouseEnter={e => { e.currentTarget.style.color = '#1960a3'; e.currentTarget.style.background = '#eef4ff'; }}
        onMouseLeave={e => { if (!open) { e.currentTarget.style.color = '#64748b'; e.currentTarget.style.background = 'transparent'; } }}>
        <span className="material-symbols-outlined">notifications</span>
        {count > 0 && (
          <span style={{ position: 'absolute', top: 4, right: 4, width: 16, height: 16, borderRadius: '50%', background: '#ef4444', color: '#fff', fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff', lineHeight: 1 }}>
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 10px)', right: 0, width: 320, background: '#fff', borderRadius: 16, boxShadow: '0 8px 32px rgba(0,32,69,0.14)', border: '1px solid #e8edf5', zIndex: 999, overflow: 'hidden', animation: 'pop-in 150ms ease' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 10px', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', fontFamily: 'Manrope,sans-serif' }}>Notifications</div>
            {count > 0 && <span style={{ fontSize: 10, fontWeight: 700, color: '#ef4444', background: '#fee2e2', padding: '2px 8px', borderRadius: 20 }}>{count} alert{count > 1 ? 's' : ''}</span>}
          </div>

          {/* List */}
          {count === 0 ? (
            <div style={{ padding: '28px 16px', textAlign: 'center' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 32, color: '#cbd5e1' }}>notifications_none</span>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>All clear — no alerts</div>
            </div>
          ) : (
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {notes.map(n => (
                <div key={n.id} style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #f8fafc' }}>
                  <button onClick={() => { setPage(n.page); setOpen(false); }}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flex: 1, padding: '12px 16px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', transition: 'background 120ms' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ width: 34, height: 34, borderRadius: 10, background: n.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 17, color: n.color }}>{n.icon}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', lineHeight: 1.4 }}>{n.title}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.sub}</div>
                    </div>
                    <span className="material-symbols-outlined" style={{ fontSize: 14, color: '#cbd5e1', flexShrink: 0, marginTop: 2 }}>chevron_right</span>
                  </button>
                  <button onClick={e => { e.stopPropagation(); setDismissed(d => [...d, n.id]); }}
                    title="Dismiss"
                    style={{ padding: '0 12px', height: '100%', border: 'none', background: 'transparent', cursor: 'pointer', color: '#cbd5e1', display: 'flex', alignItems: 'center', flexShrink: 0, transition: 'color 120ms' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                    onMouseLeave={e => e.currentTarget.style.color = '#cbd5e1'}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          {count > 0 && (
            <div style={{ padding: '10px 16px', borderTop: '1px solid #f1f5f9', textAlign: 'center' }}>
              <button onClick={() => { setDismissed(notes.map(n => n.id)); setOpen(false); }} style={{ fontSize: 11, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer' }}>Dismiss all</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export default NotificationBell;
